import React,{useState} from 'react';
import {motion} from 'framer-motion';
import {useNavigate} from 'react-router-dom';
import {useFinancial} from '../context/FinancialContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiActivity,FiBarChart2,FiAward,FiTrendingUp,FiAlertTriangle,FiCheck,FiX,FiRefreshCw,FiAlertCircle}=FiIcons;

function RiskProfile() {
  const {state,dispatch}=useFinancial();
  const navigate=useNavigate();
  const [currentQuestion,setCurrentQuestion]=useState(0);
  const [answers,setAnswers]=useState(state.riskProfile?.answers || Array(8).fill(null));
  const [showResults,setShowResults]=useState(state.riskProfile?.score !==undefined);

  // Enhanced risk assessment questions including emergency fund
  const questions=[
    {
      question: "Do you have an emergency fund covering at least 3-6 months of expenses?",
      options: [
        {text: "No emergency fund or less than 1 month coverage.",score: 1,profile: "Conservative"},
        {text: "Partial emergency fund (1-3 months coverage).",score: 2,profile: "Moderate"},
        {text: "Adequate emergency fund (3-6 months or more coverage).",score: 3,profile: "Aggressive"}
      ]
    },
    {
      question: "Where do you primarily keep your emergency funds?",
      options: [
        {text: "Gold, commodities, or illiquid investments.",score: 1,profile: "Conservative"},
        {text: "Fixed deposits or traditional savings accounts.",score: 2,profile: "Moderate"},
        {text: "High-yield savings, liquid funds, or easily accessible accounts.",score: 3,profile: "Aggressive"}
      ]
    },
    {
      question: "When you think of investments, what's more important to you?",
      options: [
        {text: "Safety of capital, even if returns are low.",score: 1,profile: "Conservative"},
        {text: "Balance of safety and growth.",score: 2,profile: "Moderate"},
        {text: "Maximum growth, even if there's higher risk.",score: 3,profile: "Aggressive"}
      ]
    },
    {
      question: "How would you react if your investment underperforms the index for 2 consecutive years?",
      options: [
        {text: "Exit immediately and switch to something else.",score: 1,profile: "Conservative"},
        {text: "Trust the fund and rebalance.",score: 3,profile: "Aggressive"},
        {text: "Change advisor.",score: 2,profile: "Moderate"}
      ]
    },
    {
      question: "How often do you monitor your mutual fund investments?",
      options: [
        {text: "Daily (very active tracking).",score: 2,profile: "Moderate"},
        {text: "Monthly (moderate tracking).",score: 2,profile: "Moderate"},
        {text: "Rarely (long-term, passive approach).",score: 3,profile: "Aggressive"}
      ]
    },
    {
      question: "If the market moves sideways (no major gains/losses) for a year, what would you do?",
      options: [
        {text: "Withdraw and invest in safer options like FDs.",score: 1,profile: "Conservative"},
        {text: "Stay invested but reassess strategy.",score: 2,profile: "Moderate"},
        {text: "Continue SIPs, trusting long-term compounding.",score: 3,profile: "Aggressive"}
      ]
    },
    {
      question: "How do you make investment decisions?",
      options: [
        {text: "Based on news/social media trends.",score: 1,profile: "Conservative"},
        {text: "Self-research and occasional advisor input.",score: 2,profile: "Moderate"},
        {text: "Rely on a financial advisor's recommendations.",score: 3,profile: "Aggressive"}
      ]
    },
    {
      question: "If your portfolio drops by 40% in a market crash, what would you do?",
      options: [
        {text: "Sell all investments to prevent further losses.",score: 1,profile: "Conservative"},
        {text: "Hold and wait for recovery but avoid adding more funds.",score: 2,profile: "Moderate"},
        {text: "Invest more to average costs, expecting a rebound.",score: 3,profile: "Aggressive"}
      ]
    },
    {
      question: "What is your ideal annual return target?",
      options: [
        {text: "10-12%",score: 1,profile: "Conservative"},
        {text: "15%",score: 2,profile: "Moderate"},
        {text: "Outperforming market",score: 3,profile: "Aggressive"}
      ]
    }
  ];

  const handleAnswer=(questionIndex,answerIndex)=> {
    const newAnswers=[...answers];
    newAnswers[questionIndex]=answerIndex;
    setAnswers(newAnswers);

    if (questionIndex < questions.length - 1) {
      setCurrentQuestion(questionIndex + 1);
    } else {
      calculateRiskProfile(newAnswers);
    }
  };

  const handlePrevious=()=> {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSkip=()=> {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      calculateRiskProfile(answers);
    }
  };

  const calculateRiskProfile=(userAnswers)=> {
    // Calculate total score
    let totalScore=0;
    let answeredQuestions=0;

    userAnswers.forEach((answer,index)=> {
      if (answer !==null) {
        totalScore +=questions[index].options[answer].score;
        answeredQuestions++;
      }
    });

    // Calculate average score if at least 5 questions answered
    if (answeredQuestions >=5) {
      const averageScore=totalScore / answeredQuestions;
      
      let riskProfile={
        answers: userAnswers,
        score: averageScore,
        category: averageScore < 1.7 ? "Conservative" : averageScore < 2.4 ? "Moderate" : "Aggressive",
        equityAllocation: averageScore < 1.7 ? 30 : averageScore < 2.4 ? 60 : 80,
        debtAllocation: averageScore < 1.7 ? 60 : averageScore < 2.4 ? 35 : 15,
        alternativesAllocation: averageScore < 1.7 ? 10 : averageScore < 2.4 ? 5 : 5
      };

      // Dispatch to update state
      dispatch({
        type: 'UPDATE_RISK_PROFILE',
        payload: riskProfile
      });

      setShowResults(true);
    } else {
      // Not enough questions answered
      alert("Please answer at least 5 questions to generate your risk profile.");
    }
  };

  const resetAssessment=()=> {
    setAnswers(Array(questions.length).fill(null));
    setCurrentQuestion(0);
    setShowResults(false);
  };

  const handleContinue=()=> {
    navigate('/goals');
  };

  const getRiskColor=(category)=> {
    switch (category) {
      case "Conservative": return "text-blue-600";
      case "Moderate": return "text-yellow-600";
      case "Aggressive": return "text-red-600";
      default: return "text-gray-600";
    }
  };

  const getRiskBgColor=(category)=> {
    switch (category) {
      case "Conservative": return "bg-blue-100";
      case "Moderate": return "bg-yellow-100";
      case "Aggressive": return "bg-red-100";
      default: return "bg-gray-100";
    }
  };

  // Check emergency fund adequacy
  const checkEmergencyFundStatus=()=> {
    const recommendedEmergencyFund=state.userProfile.monthlyExpenses * 6;
    const currentEmergencyFund=state.userProfile.emergencyFund || 0;
    const liquidAssets=(state.userProfile.assets?.bankSavings || 0) + 
                      (state.userProfile.existingInvestments?.fixedDeposits || 0) + 
                      (state.userProfile.existingInvestments?.chitFund || 0);
    
    const totalLiquidFunds=currentEmergencyFund + liquidAssets;
    const coverageMonths=totalLiquidFunds / state.userProfile.monthlyExpenses;

    return {
      isAdequate: totalLiquidFunds >= recommendedEmergencyFund * 0.8,
      coverageMonths: coverageMonths,
      totalLiquidFunds: totalLiquidFunds,
      recommendedAmount: recommendedEmergencyFund
    };
  };

  const emergencyFundStatus=checkEmergencyFundStatus();

  return (
    <motion.div
      initial={{opacity: 0,y: 20}}
      animate={{opacity: 1,y: 0}}
      className="max-w-6xl mx-auto"
    >
      <div className="flex items-center space-x-3 mb-8">
        <div className="w-10 h-10 rounded-lg bg-[#19b48d] flex items-center justify-center">
          <SafeIcon icon={FiActivity} className="text-white text-lg" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Risk Profile Assessment</h2>
          <p className="text-gray-600">Understand your investment risk tolerance to optimize your portfolio</p>
        </div>
      </div>

      {showResults ? (
        <div className="space-y-6">
          {/* Emergency Fund Status Alert */}
          {!emergencyFundStatus.isAdequate && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-6">
              <div className="flex items-start space-x-3">
                <SafeIcon icon={FiAlertCircle} className="text-red-600 text-lg mt-1" />
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-red-800 mb-2">Emergency Fund Alert</h3>
                  <p className="text-red-700 mb-3">
                    Your emergency fund covers only {emergencyFundStatus.coverageMonths.toFixed(1)} months of expenses. 
                    We recommend building it to at least 6 months before making other investments.
                  </p>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-red-800">Current Liquid Funds:</span>
                      <span className="ml-2">₹{emergencyFundStatus.totalLiquidFunds.toLocaleString()}</span>
                    </div>
                    <div>
                      <span className="font-medium text-red-800">Recommended Amount:</span>
                      <span className="ml-2">₹{emergencyFundStatus.recommendedAmount.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Risk Profile Results */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className={`w-8 h-8 rounded-lg ${getRiskBgColor(state.riskProfile.category)} flex items-center justify-center`}>
                  <SafeIcon icon={FiBarChart2} className={`${getRiskColor(state.riskProfile.category)} text-sm`} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Your Risk Profile</h3>
                  <p className="text-gray-600">Based on your responses to our assessment</p>
                </div>
              </div>
              <button
                onClick={resetAssessment}
                className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
              >
                <SafeIcon icon={FiRefreshCw} className="text-sm" />
                <span>Retake Assessment</span>
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className={`rounded-lg p-6 text-center ${getRiskBgColor(state.riskProfile.category)}`}>
                <SafeIcon icon={FiAward} className={`text-3xl mx-auto mb-2 ${getRiskColor(state.riskProfile.category)}`} />
                <h4 className="text-xl font-bold text-gray-900">{state.riskProfile.category}</h4>
                <p className="text-sm text-gray-600">Risk Profile</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-6 text-center">
                <SafeIcon icon={FiTrendingUp} className="text-3xl mx-auto mb-2 text-[#19b48d]" />
                <h4 className="text-xl font-bold text-gray-900">{state.riskProfile.score.toFixed(1)}</h4>
                <p className="text-sm text-gray-600">Risk Score</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-6 text-center">
                <div className="flex justify-center space-x-1 mb-2">
                  {[...Array(5)].map((_,i)=> (
                    <div
                      key={i}
                      className={`w-2 h-8 rounded-sm ${
                        i < Math.round(state.riskProfile.score * 5/3) ? getRiskBgColor(state.riskProfile.category) : 'bg-gray-200'
                      }`}
                    />
                  ))}
                </div>
                <p className="text-sm text-gray-600">Risk Tolerance Level</p>
              </div>
            </div>

            {/* Recommended Asset Allocation */}
            <div className="border-t border-gray-200 pt-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Recommended Asset Allocation</h4>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="h-10 w-full rounded-lg overflow-hidden flex">
                  <div
                    className="bg-blue-500 h-full flex items-center justify-center"
                    style={{width: `${state.riskProfile.equityAllocation}%`}}
                  >
                    <span className="text-white text-xs font-medium">Equity {state.riskProfile.equityAllocation}%</span>
                  </div>
                  <div
                    className="bg-green-500 h-full flex items-center justify-center"
                    style={{width: `${state.riskProfile.debtAllocation}%`}}
                  >
                    <span className="text-white text-xs font-medium">Debt {state.riskProfile.debtAllocation}%</span>
                  </div>
                  <div
                    className="bg-purple-500 h-full flex items-center justify-center"
                    style={{width: `${state.riskProfile.alternativesAllocation}%`}}
                  >
                    <span className="text-white text-xs font-medium">Alt {state.riskProfile.alternativesAllocation}%</span>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4 mt-4 text-sm">
                  <div>
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                      <p className="text-gray-700">Equity</p>
                    </div>
                    <p className="text-gray-600 ml-5">Large, mid, small cap funds</p>
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                      <p className="text-gray-700">Debt</p>
                    </div>
                    <p className="text-gray-600 ml-5">Bonds, FDs, debt funds</p>
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                      <p className="text-gray-700">Alternatives</p>
                    </div>
                    <p className="text-gray-600 ml-5">Gold, REITs, InvITs</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Risk Profile Characteristics */}
            <div className="border-t border-gray-200 pt-6 mt-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Your Risk Profile Characteristics</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h5 className="font-medium text-gray-900 mb-2">Strengths</h5>
                  <ul className="space-y-2">
                    {state.riskProfile.category==="Conservative" && (
                      <>
                        <li className="flex items-start space-x-2">
                          <SafeIcon icon={FiCheck} className="text-green-500 mt-1 flex-shrink-0" />
                          <span className="text-sm text-gray-700">Capital preservation focus</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <SafeIcon icon={FiCheck} className="text-green-500 mt-1 flex-shrink-0" />
                          <span className="text-sm text-gray-700">Lower volatility in portfolio</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <SafeIcon icon={FiCheck} className="text-green-500 mt-1 flex-shrink-0" />
                          <span className="text-sm text-gray-700">Steady, predictable returns</span>
                        </li>
                      </>
                    )}
                    {state.riskProfile.category==="Moderate" && (
                      <>
                        <li className="flex items-start space-x-2">
                          <SafeIcon icon={FiCheck} className="text-green-500 mt-1 flex-shrink-0" />
                          <span className="text-sm text-gray-700">Balanced risk-reward approach</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <SafeIcon icon={FiCheck} className="text-green-500 mt-1 flex-shrink-0" />
                          <span className="text-sm text-gray-700">Flexibility to adjust to market conditions</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <SafeIcon icon={FiCheck} className="text-green-500 mt-1 flex-shrink-0" />
                          <span className="text-sm text-gray-700">Good potential for long-term growth</span>
                        </li>
                      </>
                    )}
                    {state.riskProfile.category==="Aggressive" && (
                      <>
                        <li className="flex items-start space-x-2">
                          <SafeIcon icon={FiCheck} className="text-green-500 mt-1 flex-shrink-0" />
                          <span className="text-sm text-gray-700">Maximum growth potential</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <SafeIcon icon={FiCheck} className="text-green-500 mt-1 flex-shrink-0" />
                          <span className="text-sm text-gray-700">Long-term wealth creation focus</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <SafeIcon icon={FiCheck} className="text-green-500 mt-1 flex-shrink-0" />
                          <span className="text-sm text-gray-700">Ability to withstand market volatility</span>
                        </li>
                      </>
                    )}
                  </ul>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h5 className="font-medium text-gray-900 mb-2">Considerations</h5>
                  <ul className="space-y-2">
                    {state.riskProfile.category==="Conservative" && (
                      <>
                        <li className="flex items-start space-x-2">
                          <SafeIcon icon={FiAlertTriangle} className="text-yellow-500 mt-1 flex-shrink-0" />
                          <span className="text-sm text-gray-700">May not beat inflation in long-term</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <SafeIcon icon={FiAlertTriangle} className="text-yellow-500 mt-1 flex-shrink-0" />
                          <span className="text-sm text-gray-700">Limited growth potential</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <SafeIcon icon={FiAlertTriangle} className="text-yellow-500 mt-1 flex-shrink-0" />
                          <span className="text-sm text-gray-700">May require higher contributions to reach goals</span>
                        </li>
                      </>
                    )}
                    {state.riskProfile.category==="Moderate" && (
                      <>
                        <li className="flex items-start space-x-2">
                          <SafeIcon icon={FiAlertTriangle} className="text-yellow-500 mt-1 flex-shrink-0" />
                          <span className="text-sm text-gray-700">Medium volatility exposure</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <SafeIcon icon={FiAlertTriangle} className="text-yellow-500 mt-1 flex-shrink-0" />
                          <span className="text-sm text-gray-700">May require periodic rebalancing</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <SafeIcon icon={FiAlertTriangle} className="text-yellow-500 mt-1 flex-shrink-0" />
                          <span className="text-sm text-gray-700">Need for active portfolio monitoring</span>
                        </li>
                      </>
                    )}
                    {state.riskProfile.category==="Aggressive" && (
                      <>
                        <li className="flex items-start space-x-2">
                          <SafeIcon icon={FiAlertTriangle} className="text-yellow-500 mt-1 flex-shrink-0" />
                          <span className="text-sm text-gray-700">High short-term volatility</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <SafeIcon icon={FiAlertTriangle} className="text-yellow-500 mt-1 flex-shrink-0" />
                          <span className="text-sm text-gray-700">Requires patience during market downturns</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <SafeIcon icon={FiAlertTriangle} className="text-yellow-500 mt-1 flex-shrink-0" />
                          <span className="text-sm text-gray-700">Need for longer investment horizon (7+ years)</span>
                        </li>
                      </>
                    )}
                  </ul>
                </div>
              </div>
            </div>

            {/* Continue Button */}
            <div className="flex justify-end pt-6 border-t border-gray-200 mt-6">
              <button
                onClick={handleContinue}
                className="px-8 py-3 bg-[#19b48d] text-white font-medium rounded-lg hover:bg-[#16a085] transition-colors duration-200"
              >
                Continue to Goals
              </button>
            </div>
          </div>

          {/* Recommended Investment Products */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Recommended Investment Products</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {state.riskProfile.category==="Conservative" && (
                <>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">Fixed Income</h4>
                    <ul className="space-y-1 text-sm text-gray-700">
                      <li>• Short-term debt funds</li>
                      <li>• Corporate bond funds</li>
                      <li>• Banking & PSU debt funds</li>
                      <li>• Fixed deposits</li>
                    </ul>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">Hybrid</h4>
                    <ul className="space-y-1 text-sm text-gray-700">
                      <li>• Conservative hybrid funds</li>
                      <li>• Equity savings funds</li>
                      <li>• Arbitrage funds</li>
                    </ul>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">Equity</h4>
                    <ul className="space-y-1 text-sm text-gray-700">
                      <li>• Large cap funds (limited)</li>
                      <li>• Index funds</li>
                      <li>• Bluechip funds</li>
                    </ul>
                  </div>
                </>
              )}
              {state.riskProfile.category==="Moderate" && (
                <>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">Fixed Income</h4>
                    <ul className="space-y-1 text-sm text-gray-700">
                      <li>• Medium-term debt funds</li>
                      <li>• Dynamic bond funds</li>
                      <li>• Short duration funds</li>
                    </ul>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">Hybrid</h4>
                    <ul className="space-y-1 text-sm text-gray-700">
                      <li>• Balanced advantage funds</li>
                      <li>• Aggressive hybrid funds</li>
                      <li>• Multi-asset allocation funds</li>
                    </ul>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">Equity</h4>
                    <ul className="space-y-1 text-sm text-gray-700">
                      <li>• Large & mid cap funds</li>
                      <li>• Flexi cap funds</li>
                      <li>• Value funds</li>
                    </ul>
                  </div>
                </>
              )}
              {state.riskProfile.category==="Aggressive" && (
                <>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">Equity</h4>
                    <ul className="space-y-1 text-sm text-gray-700">
                      <li>• Mid & small cap funds</li>
                      <li>• Sectoral/thematic funds</li>
                      <li>• Focused equity funds</li>
                    </ul>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">Hybrid</h4>
                    <ul className="space-y-1 text-sm text-gray-700">
                      <li>• Aggressive hybrid funds</li>
                      <li>• Multi-asset allocation funds</li>
                    </ul>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">Alternatives</h4>
                    <ul className="space-y-1 text-sm text-gray-700">
                      <li>• REITs & InvITs</li>
                      <li>• International funds</li>
                      <li>• Gold ETFs/funds</li>
                    </ul>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          {/* Progress Bar */}
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Question {currentQuestion + 1} of {questions.length}</span>
              <span className="text-sm font-medium text-[#19b48d]">{Math.round(((currentQuestion + 1) / questions.length) * 100)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div
                className="bg-[#19b48d] h-2.5 rounded-full transition-all duration-300"
                style={{width: `${((currentQuestion + 1) / questions.length) * 100}%`}}
              ></div>
            </div>
          </div>

          {/* Question */}
          <div className="p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-6">{questions[currentQuestion].question}</h3>
            <div className="space-y-3">
              {questions[currentQuestion].options.map((option,index)=> (
                <button
                  key={index}
                  onClick={()=> handleAnswer(currentQuestion,index)}
                  className={`w-full text-left p-4 rounded-lg border transition-colors duration-200 ${
                    answers[currentQuestion]===index
                      ? 'bg-[#19b48d]/10 border-[#19b48d]'
                      : 'bg-white border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-6 h-6 rounded-full border flex items-center justify-center ${
                      answers[currentQuestion]===index ? 'border-[#19b48d]' : 'border-gray-300'
                    }`}>
                      {answers[currentQuestion]===index && (
                        <div className="w-3 h-3 rounded-full bg-[#19b48d]"></div>
                      )}
                    </div>
                    <span className="text-gray-800">{option.text}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Navigation */}
          <div className="p-6 border-t border-gray-200 flex items-center justify-between">
            <button
              onClick={handlePrevious}
              disabled={currentQuestion===0}
              className={`px-4 py-2 text-sm rounded-lg ${
                currentQuestion===0
                  ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                  : 'text-gray-600 bg-gray-100 hover:bg-gray-200'
              }`}
            >
              Previous
            </button>
            <div className="flex items-center space-x-3">
              <button
                onClick={handleSkip}
                className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Skip
              </button>
              {currentQuestion===questions.length - 1 ? (
                <button
                  onClick={()=> calculateRiskProfile(answers)}
                  className="px-6 py-2 bg-[#19b48d] text-white rounded-lg hover:bg-[#16a085]"
                >
                  Complete
                </button>
              ) : (
                <button
                  onClick={handleSkip}
                  className="px-6 py-2 bg-[#19b48d] text-white rounded-lg hover:bg-[#16a085]"
                >
                  Next
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
}

export default RiskProfile;